import { isEqual } from "lodash";
import type { StateObservable } from "redux-observable";
import { merge, of } from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
  timeout,
} from "rxjs/operators";
import { isActionOf, type PayloadAction } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { ErrorResponseMessages } from "@/enums/ErrorResponseMessages.enum";
import * as configActions from "../../state/config/config.actions";
import {
  authService,
  documentMetadata,
} from "../../state/config/config.selector";
import { AppMode } from "../../state/mode/mode.reducer";
import type { AppState } from "../../state/root.reducer";
import * as templatesActions from "../../state/templates/templates.actions";
import type {
  UserInvitePayload,
  UserInviteResponse,
} from "../../state/user/user.actions";
import {
  authorizationHeader,
  isPasswordRequired as selectIsPasswordRequired,
  userId,
  userToken,
} from "../../state/user/user.selector";
import { selectDashboardSelectedProject } from "../../state/workspaces/projects.selector";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import { DEFAULT_SCHEMA_VERSION } from "../../state/workspaces/workspaces.reducer";
import {
  selectDashboardIncludeHiddenWorkspaces,
  selectDashboardWorkspaceDetailsById,
  selectFullscreenWorkspaceId,
  selectLiveWorkspaceIds,
  selectWorkspaceById,
  selectWorkspacesSearchLabelsString,
  selectWorkspacesSearchTerm,
} from "../../state/workspaces/workspaces.selector";
import { Localized } from "../../strings";
import {
  defaultWorkspaceName,
  getCreateBlankWorkspacePayload,
  getCreateFromTemplatePayload,
} from "../../utils/create.workspace.utils";
import { defaultDashboardPageSize } from "../../utils/defaultDashboardPageSize";
import { isErrorResponse } from "../../utils/error.utils";
import { isAjaxError } from "../dependencies/ajaxRequests";
import {
  waitForAppState,
  waitForConfigLoaded,
  waitForLicense,
  waitForToken,
} from "../helpers/waitForState";
import type { AppEpic } from "../types";
import type { CreateWorkspaceRequestMetadata } from "./api/workspaces.types";

export const getDashboardLabelsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.getDashboardLabels.request)),
    waitForToken(state$),
    mergeMap(() =>
      documentIdv3Requests
        .getDashboardLabels({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          userId: userId(state$.value),
        })
        .pipe(
          map((labels) => workspacesActions.getDashboardLabels.success(labels)),
          catchError((error) =>
            of(workspacesActions.getDashboardLabels.failure(error)),
          ),
        ),
    ),
  );

export const loadWorkspacesPageEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.loadWorkspaces.request)),
    distinctUntilChanged((prev, curr) => {
      if (!curr.payload.offset) return false;
      return isEqual(prev, curr);
    }),
    waitForToken(state$),
    mergeMap((action) => {
      return documentIdv3Requests
        .getDashboardWorkspacesPaged({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          userId: userId(state$.value),
          ...action.payload,
          limit: action.payload.limit ?? defaultDashboardPageSize,
        })
        .pipe(
          map((workspacesPagingResult) =>
            workspacesActions.loadWorkspaces.success(workspacesPagingResult),
          ),
          catchError((error) =>
            of(
              workspacesActions.loadWorkspaces.failure({
                error,
                message: Localized.string("ERROR.SOMETHING_WENT_WRONG"),
                requestAction: action,
              }),
            ),
          ),
        );
    }),
  );

export const loadHiddenWorkspacesPageEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.loadHiddenWorkspaces.request)),
    waitForToken(state$),
    switchMap((action) => {
      return documentIdv3Requests
        .getDashboardHiddenWorkspacesPaged({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          userId: userId(state$.value),
          ...action.payload,
          limit: action.payload.limit ?? defaultDashboardPageSize,
        })
        .pipe(
          map((workspacesPagingResult) =>
            workspacesActions.loadHiddenWorkspaces.success(
              workspacesPagingResult,
            ),
          ),
          catchError((error) =>
            of(workspacesActions.loadHiddenWorkspaces.failure(error)),
          ),
        );
    }),
  );

const getDashboardWorkspacePage = (state$: StateObservable<AppState>) => () => {
  const selectedProject = selectDashboardSelectedProject(state$.value);
  const includeHidden = selectDashboardIncludeHiddenWorkspaces(state$.value);
  const labels = selectWorkspacesSearchLabelsString(state$.value);
  const name = selectWorkspacesSearchTerm(state$.value);

  return selectedProject
    ? workspacesActions.getWorkspacesOfProject.request({
        projectId: selectedProject.id,
        labels,
        name,
      })
    : workspacesActions.loadWorkspaces.request({
        includeHidden,
        labels,
        name,
        offset: 0,
      });
};

export const returnToDashboardEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf([workspacesActions.showDashboard])),
    throttleTime(1000 * 60 * 5), // 5 minutes
    map(getDashboardWorkspacePage(state$)),
  );

export const refetchWorkspaceDetailsEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(
      isActionOf([
        workspacesActions.addWorkspaceToProject.success,
        workspacesActions.removeWorkspaceFromProject.success,
        workspacesActions.updateWorkspaceGlobalAccess.success,
      ]),
    ),
    map((action) => {
      return workspacesActions.getWorkspaceDetails.request(
        action.payload.workspaceId,
      );
    }),
  );

export const workspaceDetailsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests, sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.getWorkspaceDetails.request)),
    waitForToken(state$),
    waitForLicense(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .getRawWorkspaceMetadata(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload,
        )
        .pipe(
          // TODO: Changing workspace to template is not sending proper actions to activate it, bigger refactor of changing workspace to template is needed to create proper actions and epics handling it.
          // NOTE: Implemented as a low regression solution to fix fast an issue caused by opened workspaces not getting info about becoming template.
          tap((myWorkspaceMetadata) => {
            const currentMyWorkspaceMetadata = selectWorkspaceById(
              state$.value,
              myWorkspaceMetadata.workspaceId,
            );

            // TODO: should this be bi-directional, i.e. also inform the workspace when it is un-"templated"?
            if (
              myWorkspaceMetadata.templateId &&
              !currentMyWorkspaceMetadata?.templateId
            ) {
              sendMessageToWorkspace(myWorkspaceMetadata.workspaceId, {
                action: "WORKSPACE_DETAILS_UPDATED",
                details: {
                  workspaceName: myWorkspaceMetadata.workspaceName,
                },
              });
            }
          }),
          mergeMap((myWorkspaceMetadata) => {
            if (myWorkspaceMetadata.workspaceType === "DELETED") {
              return of(
                workspacesActions.getWorkspaceDetails.failure({
                  error: new Error(Localized.string("ERROR.WORKSPACE_DELETED")),
                  workspaceId: action.payload,
                  requestAction: action,
                  message: Localized.string("ERROR.WORKSPACE_HAS_BEEN_DELETED"),
                }),
              );
            }

            const workspaceDetails = documentIdv3Requests
              .getWorkspaceRoles(
                documentMetadata(state$.value),
                userToken(state$.value),
                myWorkspaceMetadata.workspaceId,
              )
              .pipe(
                mergeMap((workspaceRoles) =>
                  of(
                    workspacesActions.getWorkspaceDetails.success(
                      myWorkspaceMetadata,
                    ),
                    workspacesActions.getWorkspaceRoles.success({
                      workspaceId: myWorkspaceMetadata.workspaceId,
                      workspaceRoles,
                    }),
                  ),
                ),
              );

            if (
              myWorkspaceMetadata.templateId &&
              state$.value.context.workspaces.activeOption === "OPTIONS"
            ) {
              return merge(
                workspaceDetails,
                of(
                  templatesActions.fetchTemplateDetails.request(
                    myWorkspaceMetadata.templateId,
                  ),
                ),
              );
            }

            return workspaceDetails;
          }),
          catchError((error) =>
            of(
              workspacesActions.getWorkspaceDetails.failure({
                error,
                workspaceId: action.payload,
                requestAction: action,
                message: Localized.string("ERROR.VERIFY_WORKSPACE_ID"),
              }),
            ),
          ),
        ),
    ),
  );

export const autoUpdateWorkspaceDetailsOnShareEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.activateDashboardOption)),
    filter(
      (a) =>
        [
          DashboardOption.SHARE,
          DashboardOption.INFO,
          DashboardOption.PERMISSIONS,
          DashboardOption.OPTIONS,
          DashboardOption.EXPANDED_SHARE,
        ].includes(a.payload.optionType) && !!a.payload.workspaceId,
    ),
    map(
      (a) =>
        workspacesActions.getWorkspaceDetails.request(a.payload.workspaceId!), // cannot be undefined/null because of filter above
    ),
  );

export const createWorkspaceEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.createWorkspace.request)),
    waitForToken(state$),
    waitForLicense(state$),
    throttleTime(500),
    mergeMap((action) => {
      // Set default payload values if not provided
      const completeActionPayload: CreateWorkspaceRequestMetadata = {
        ...action.payload,
        workspaceName: action.payload.workspaceName || defaultWorkspaceName,
        workspaceType: "HOYLU",
        schemaVersion: DEFAULT_SCHEMA_VERSION,
        /* If we are on the Project view on the Dashboard - checking via store property "selectedProject"
           we want to add a newly created ws immediately to that Project by setting "containerId" */
        containerId: state$.value.context.workspaces.selectedProject?.id,
      };

      return documentIdv3Requests
        .createWorkspace(
          documentMetadata(state$.value),
          userToken(state$.value),
          completeActionPayload,
        )
        .pipe(
          map((response) =>
            workspacesActions.createWorkspace.success(response),
          ),
          catchError((error) => {
            let message = Localized.string("ERROR.CANNOT_CREATE_WORKSPACE");

            if (state$.value.context.workspaces.selectedProject) {
              if (isAjaxError(error)) {
                console.dir(error);
                switch (error.status) {
                  case 402:
                    message = [
                      Localized.string("ERROR.PROJECTS.CANNOT_ADD_WORKSPACE"),
                      isErrorResponse(error.response)
                        ? error.response.code ===
                          ErrorResponseMessages.WorkspaceLimitReached
                          ? Localized.string(
                              "ERROR.PROJECTS.REASON_MAX_WORKSPACES",
                            )
                          : error.response.message
                        : String(error.response),
                    ].join(" ");
                    break;
                  case 403:
                    message = Localized.string(
                      "ERROR.PROJECTS.INSUFFICIENT_PERMISSIONS_TO_ADD_WORKSPACE",
                      String(error.response),
                    );
                    break;
                }
              }
            }
            return of(
              workspacesActions.createWorkspace.failure({
                requestAction: action,
                error,
                message,
              }),
            );
          }),
        );
    }),
  );

export const duplicateWorkspaceEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.duplicateWorkspace.request)),
    waitForToken(state$),
    waitForLicense(state$),
    throttleTime(500),
    mergeMap((action) => {
      const {
        workspaceId,
        password,
        workspaceName,
        globalAccess,
        newPassword,
        containerId,
        open,
      } = action.payload;

      return documentIdv3Requests
        .duplicateWorkspace(
          documentMetadata(state$.value),
          userToken(state$.value),
          /* source workspace */
          workspaceId,
          password,
          /* new workspace */
          {
            workspaceName,
            globalAccess,
            password: newPassword || null,
            containerId,
          },
        )
        .pipe(
          map((myWorkspaceMetadata) =>
            workspacesActions.duplicateWorkspace.success({
              myWorkspaceMetadata,
              open,
            }),
          ),
          catchError((error) => {
            let message = Localized.string("ERROR.CANNOT_DUPLICATE_WORKSPACE");

            const isAssignedToProject = !!containerId;
            if (isAssignedToProject) {
              if (isAjaxError(error)) {
                console.dir(error);
                switch (error.status) {
                  case 402:
                    message = [
                      Localized.string("ERROR.PROJECTS.CANNOT_ADD_WORKSPACE"),
                      isErrorResponse(error.response)
                        ? error.response.code ===
                          ErrorResponseMessages.WorkspaceLimitReached
                          ? Localized.string(
                              "ERROR.PROJECTS.REASON_MAX_WORKSPACES",
                            )
                          : error.response.message
                        : String(error.response),
                    ].join(" ");
                    break;
                  case 403:
                    message = Localized.string(
                      "ERROR.PROJECTS.INSUFFICIENT_PERMISSIONS_TO_ADD_WORKSPACE",
                      String(error.response),
                    );
                    break;
                }
              }
            }
            const passwordError =
              error?.response?.code === ErrorResponseMessages.InvalidPassword;

            if (passwordError) {
              message = Localized.string("ERROR.INCORRECT_PASSWORD");
            }

            const failureAction = workspacesActions.duplicateWorkspace.failure({
              requestAction: action,
              error,
              clearError: !passwordError, // Want to keep the duplicate dialog after user clicks OK if incorrect password
              message: message,
            });
            return of(failureAction);
          }),
        );
    }),
  );

export const openWorkspaceAfterDuplicateIfRequestedEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.duplicateWorkspace.success)),
    filter((a) => !!a.payload.open),
    map((a) =>
      workspacesActions.editWorkspace({
        workspaceId: a.payload.myWorkspaceMetadata.workspaceId,
      }),
    ),
  );

export const queueCreateWorkspaceEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.queueCreateWorkspace)),
    waitForToken(state$),
    waitForLicense(state$),
    map(() =>
      workspacesActions.createWorkspace.request(
        getCreateBlankWorkspacePayload(),
      ),
    ),
  );

export const upsertWorkspaceUsersEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.upsertWorkspaceUsers.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .upsertWorkspaceUserPermissions(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload.workspaceId,
          action.payload.userEmails,
          action.payload.permission,
        )
        .pipe(
          map((workspaceRoles) =>
            workspacesActions.upsertWorkspaceUsers.success({
              workspaceId: action.payload.workspaceId,
              workspaceRoles,
            }),
          ),
          catchError((error) =>
            of(
              workspacesActions.upsertWorkspaceUsers.failure({
                requestAction: action,
                error,
                message: Localized.string("ERROR.CANNOT_APPLY_CHANGES"),
              }),
            ),
          ),
        ),
    ),
  );

export const removeWorkspaceUserEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.removeWorkspaceUsers.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .removeUsersFromWorkspacePermissions(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload.workspaceId,
          action.payload.userEmails,
        )
        .pipe(
          map((response) =>
            workspacesActions.removeWorkspaceUsers.success({
              workspaceId: action.payload.workspaceId,
              workspaceRoles: response,
            }),
          ),
          catchError((error) =>
            of(
              workspacesActions.removeWorkspaceUsers.failure({
                requestAction: action,
                error,
                message: Localized.string("ERROR.CANNOT_APPLY_CHANGES"),
              }),
            ),
          ),
        ),
    ),
  );

export const updateWorkspaceGlobalAccessEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.updateWorkspaceGlobalAccess.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .patchWorkspaceGlobalAccess({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          ...action.payload,
        })
        .pipe(
          map(() =>
            workspacesActions.updateWorkspaceGlobalAccess.success(
              action.payload,
            ),
          ),
          catchError((error) =>
            of(
              workspacesActions.updateWorkspaceGlobalAccess.failure({
                requestAction: action,
                error,
                message: Localized.string("ERROR.CANNOT_APPLY_CHANGES"),
              }),
            ),
          ),
        ),
    ),
  );

export const updateWorkspaceLabelsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.updateWorkspaceLabels.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .patchWorkspaceLabels({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          workspaceId: action.payload.workspaceId,
          labels: action.payload.labels,
        })
        .pipe(
          map(() =>
            workspacesActions.updateWorkspaceLabels.success(action.payload),
          ),
          catchError((error) =>
            of(
              workspacesActions.updateWorkspaceLabels.failure({
                requestAction: action,
                error,
                message: Localized.string("ERROR.CANNOT_APPLY_CHANGES"),
              }),
            ),
          ),
        ),
    ),
  );

export const autoEditCreatedWorkspaceEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(isActionOf([workspacesActions.createWorkspace.success])),
    map((a) =>
      workspacesActions.editWorkspace({
        workspaceId: a.payload.workspaceId,
      }),
    ),
  );

export const webexPinWorkspaceOnEditEpic: AppEpic = (
  actions$,
  state$,
  { log, setWebexShareUrl },
) =>
  actions$.pipe(
    filter(isActionOf(workspacesActions.editWorkspace)),
    filter(() => !!state$.value.context.config.webexMode?.isInSelectorMode),
    tap(async (action) => {
      const workspace = selectWorkspaceById(
        state$.value,
        action.payload.workspaceId,
      );
      try {
        await setWebexShareUrl(
          action.payload.workspaceId,
          workspace?.workspaceName || "Hoylu Workspace",
        );
      } catch (e) {
        log.error("webexPinWorkspaceOnEditEpic: error:", { exception: e });
      }
    }),
    ignoreElements(),
  );

export const applyTemplateToCreatedWorkspaceEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.createWorkspace.success)),
    filter((a) => a.payload.module.name !== null),
    mergeMap((a) => {
      return of(a).pipe(
        waitForAppState(state$, (s) => {
          return (
            selectDashboardWorkspaceDetailsById(s, a.payload.workspaceId)
              ?.isDocumentReady === true
          );
        }),
      );
    }),
    map((a) =>
      workspacesActions.applyTemplateToWorkspace({
        workspaceId: a.payload.workspaceId,
        templateName: a.payload.module.name!, // NOTE: null module names filtered above.
      }),
    ),
  );

export const webexDetectedEpic: AppEpic = (
  action$,
  state$,
  { log, setWebexShareUrl },
) =>
  action$.pipe(
    filter(isActionOf(configActions.webexDetected)),
    // don't allow sharing Dashboard
    filter(
      () =>
        state$.value.mode === AppMode.LOADING_WORKSPACE ||
        state$.value.mode === AppMode.EDITING,
    ),
    // only call setWebexShareUrl when app is in "select workspace mode" (popup in spaces or add app flow in meetings)
    // because webex SDK seems to hang up if it is called in an already pinned or shared workspace
    filter((a) => a.payload.isInSelectorMode),
    map(() => selectFullscreenWorkspaceId(state$.value)),
    filter((fullscreenWorkspaceId) => fullscreenWorkspaceId !== null),
    map((fullscreenWorkspaceId) =>
      selectWorkspaceById(state$.value, fullscreenWorkspaceId),
    ),
    filter((fullscreenWorkspace) => !!fullscreenWorkspace),
    tap(async (fullscreenWorkspace) => {
      try {
        await setWebexShareUrl(
          fullscreenWorkspace.workspaceId,
          fullscreenWorkspace.workspaceName || "Hoylu Workspace",
        );
      } catch (e) {
        log.error("webexDetectedEpic: error:", { exception: e });
      }
    }),
    ignoreElements(),
  );

export const postApplyTemplateToWorkspaceEpic: AppEpic = (
  action$,
  _state$,
  { sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.applyTemplateToWorkspace)),
    tap((a) =>
      sendMessageToWorkspace(a.payload.workspaceId, {
        action: "APPLY_TEMPLATE",
        templateName: a.payload.templateName,
      }),
    ),
    ignoreElements(),
  );

export const postHandleFullscreenEpic: AppEpic = (
  action$,
  state$,
  { sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.handleFullscreenChange)),
    tap(() => {
      const liveWorkspaceIds = selectLiveWorkspaceIds(state$.value);
      liveWorkspaceIds.forEach((workspaceId) => {
        sendMessageToWorkspace(workspaceId, {
          action: "TOGGLE_FULLSCREEN",
        });
      });
    }),
    ignoreElements(),
  );

export const toggleWorkspaceVisibilityEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.toggleWorkspaceVisibility.request)),
    waitForToken(state$),
    mergeMap((action) => {
      const workspace = selectWorkspaceById(
        state$.value,
        action.payload.workspaceId,
      );

      return documentIdv3Requests
        .setDashboardWorkspaceVisibility({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          userId: userId(state$.value),
          workspaceId: action.payload.workspaceId,
          isHidden: !workspace?.isHidden,
        })
        .pipe(
          map(() =>
            workspacesActions.toggleWorkspaceVisibility.success(action.payload),
          ),
          catchError((error) =>
            of(
              workspacesActions.toggleWorkspaceVisibility.failure({
                error,
                requestAction: action,
                message: Localized.string(
                  `ERROR.CANNOT_${
                    workspace?.isHidden ? "UNHIDE" : "HIDE"
                  }_WORKSPACE`,
                ),
              }),
            ),
          ),
        );
    }),
  );

export const leaveWorkspaceEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.leaveWorkspace.request)),
    waitForToken(state$),
    mergeMap((a) =>
      documentIdv3Requests
        .removeFromMyWorkspaces(
          documentMetadata(state$.value),
          userToken(state$.value),
          userId(state$.value),
          a.payload.workspaceId,
        )
        .pipe(
          mergeMap(() => {
            const selectedProject = selectDashboardSelectedProject(
              state$.value,
            );
            const successActions: PayloadAction<any, any>[] = [
              workspacesActions.leaveWorkspace.success(a.payload),
            ];
            if (selectedProject) {
              successActions.push(
                workspacesActions.getWorkspaceDetails.request(
                  a.payload.workspaceId,
                ),
              );
            }
            return of(...successActions);
          }),
          catchError((error) =>
            of(
              workspacesActions.leaveWorkspace.failure({
                error,
                requestAction: a,
                message: Localized.string("ERROR.CANNOT_DELETE_WORKSPACE"),
              }),
            ),
          ),
        ),
    ),
  );

export const deleteWorkspaceEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.deleteWorkspace.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .deleteWorkspace({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          ...action.payload,
        })
        .pipe(
          map(() =>
            workspacesActions.deleteWorkspace.success({
              workspaceId: action.payload.workspaceId,
            }),
          ),
          catchError((error) =>
            of(
              workspacesActions.deleteWorkspace.failure({
                error,
                requestAction: action,
                message: Localized.string("ERROR.CANNOT_DELETE_WORKSPACE"),
              }),
            ),
          ),
        ),
    ),
  );

export const setWorkspacePasswordEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.setWorkspacePassword.request)),
    waitForToken(state$),
    mergeMap((requestAction) =>
      documentIdv3Requests
        .patchWorkspacePassword({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          workspaceId: requestAction.payload.workspaceId,
          currentPassword: requestAction.payload.currPassword,
          newPassword: requestAction.payload.newPassword,
        })
        .pipe(
          map(() =>
            workspacesActions.setWorkspacePassword.success(
              requestAction.payload,
            ),
          ),
          catchError((error) => {
            const message =
              error.status === 403
                ? Localized.string("ERROR.INCORRECT_WORKSPACE_PASSWORD")
                : Localized.string("ERROR.CANNOT_SET_PASSWORD");
            return of(
              workspacesActions.setWorkspacePassword.failure({
                error,
                message,
                requestAction,
              }),
            );
          }),
        ),
    ),
  );

export const removeWorkspacePasswordEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.removeWorkspacePassword.request)),
    waitForToken(state$),
    mergeMap((requestAction) =>
      documentIdv3Requests
        .patchWorkspacePassword({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          workspaceId: requestAction.payload.workspaceId,
          currentPassword: requestAction.payload.currPassword,
          newPassword: null,
        })
        .pipe(
          map(() =>
            workspacesActions.removeWorkspacePassword.success(
              requestAction.payload,
            ),
          ),
          catchError((error) => {
            const message =
              error.status === 403
                ? Localized.string("ERROR.INCORRECT_WORKSPACE_PASSWORD")
                : Localized.string("ERROR.CANNOT_REMOVE_PASSWORD");
            return of(
              workspacesActions.removeWorkspacePassword.failure({
                error,
                message,
                requestAction,
              }),
            );
          }),
        ),
    ),
  );

export const createWorkspaceFromTemplateEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.createWorkspaceFromTemplate)),
    map((action) => {
      const isPasswordRequired = selectIsPasswordRequired(state$.value);
      const { workspaceId, hasPassword } = action.payload;

      if (hasPassword || isPasswordRequired) {
        return workspacesActions.activateDashboardOption({
          optionType: DashboardOption.CREATE_FROM_TEMPLATE,
        });
      }

      const selectedProjectId = selectDashboardSelectedProject(
        state$.value,
      )?.id;
      return workspacesActions.duplicateWorkspace.request(
        getCreateFromTemplatePayload(workspaceId, selectedProjectId),
      );
    }),
  );

export const inviteToWorkspaceEpic: AppEpic = (action$, state$, { postJSON }) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.inviteToWorkspace.request)),
    waitForToken(state$),
    waitForConfigLoaded(state$),
    mergeMap(({ payload }) =>
      // TODO: extract postJSON to a request file for auth service like we do with documentId service
      postJSON<UserInvitePayload, UserInviteResponse>(
        `${authService(state$.value)}/invite`,
        payload.invite,
        authorizationHeader(state$.value),
      ).pipe(
        map((response) => {
          const invitesSent = Object.entries(response)
            .filter(([, result]) => result.startsWith("200"))
            .map(([email]) => email);
          if (invitesSent.length === 0) {
            return workspacesActions.inviteToWorkspace.failure(
              Error("No invites sent"),
            );
          }
          return workspacesActions.upsertWorkspaceUsers.request({
            workspaceId: payload.workspaceId,
            permission: payload.newUsersPermission,
            userEmails: invitesSent,
          });
        }),
        catchError((error) => {
          console.warn("Unable to invite users:", payload, error);
          // For now we don't really care about the error handling here
          return of(workspacesActions.inviteToWorkspace.failure(error));
        }),
      ),
    ),
  );

export const updateWorkspaceNameEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests, sendMessageToWorkspace, updateDocumentTitle },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.updateWorkspaceName.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .patchWorkspaceName({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          ...action.payload,
        })
        .pipe(
          tap(() => {
            // NOTE: Only update the document title when in EDIT mode. Keeps "Hoylu" Browser's tab name while being on Dashboard.
            if (state$.value.mode === AppMode.EDITING) {
              updateDocumentTitle(action.payload.workspaceName);
            }
          }),
          tap(() => {
            sendMessageToWorkspace(action.payload.workspaceId, {
              action: "WORKSPACE_DETAILS_UPDATED",
              details: {
                workspaceName: action.payload.workspaceName,
              },
            });
          }),
          map(() =>
            workspacesActions.updateWorkspaceName.success(action.payload),
          ),
          catchError((error) =>
            of(
              workspacesActions.updateWorkspaceName.failure({
                error,
                requestAction: action,
                message: "Failed to update workspace name",
              }),
            ),
          ),
        ),
    ),
  );

export const createWorkspaceFromTemplateIdEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests, templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.createWorkspaceFromTemplateId.request)),
    mergeMap((action) =>
      templateRequests
        .getTemplateById(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload.templateId,
        )
        .pipe(
          mergeMap(({ workspaceId }) =>
            documentIdv3Requests.getRawWorkspaceMetadata(
              documentMetadata(state$.value),
              userToken(state$.value),
              workspaceId,
            ),
          ),
          map((response) =>
            workspacesActions.createWorkspaceFromTemplate(response),
          ),
          catchError((error) =>
            of(
              workspacesActions.createWorkspaceFromTemplateId.failure({
                error,
                requestAction: action,
                message: "Failed to get template",
              }),
            ),
          ),
        ),
    ),
  );

export const updateThumbnailEpic: AppEpic = (
  action$,
  state$,
  { uploadThumbnail },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.uploadThumbnail.request)),
    waitForToken(state$),
    waitForConfigLoaded(state$),
    mergeMap((action) =>
      uploadThumbnail(
        state$.value,
        action.payload.workspaceId,
        action.payload.file,
      ).pipe(
        map((url) =>
          workspacesActions.uploadThumbnail.success({
            workspaceId: action.payload.workspaceId,
            url,
          }),
        ),
        catchError((error) =>
          // NOTE: Since the uploadService does not use RxJS ajax we cannot use the retry-able Actions behavior to refresh tokens on 403. It's a limitation of the current implementation.
          of(workspacesActions.uploadThumbnail.failure(error)),
        ),
      ),
    ),
  );

export const removeThumbnailEpic: AppEpic = (
  action$,
  state$,
  { removeThumbnail },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.removeThumbnail.request)),
    waitForToken(state$),
    waitForConfigLoaded(state$),
    mergeMap((action) =>
      removeThumbnail(state$.value, action.payload.workspaceId).pipe(
        map(() =>
          workspacesActions.removeThumbnail.success({
            workspaceId: action.payload.workspaceId,
          }),
        ),
        catchError((error) =>
          of(workspacesActions.removeThumbnail.failure(error)),
        ),
      ),
    ),
  );

export const snapshotEpic: AppEpic = (
  action$,
  _state$,
  { sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.workspaceSnapshot.request)),
    tap((action) => {
      sendMessageToWorkspace(action.payload.workspaceId, {
        action: "TAKE_SNAPSHOT",
      });
    }),
    mergeMap(() =>
      action$.pipe(
        filter(
          isActionOf([
            workspacesActions.workspaceSnapshot.success,
            workspacesActions.workspaceSnapshot.failure,
          ]),
        ),
        takeUntil(
          action$.pipe(
            filter(
              isActionOf([
                workspacesActions.workspaceSnapshot.success,
                workspacesActions.workspaceSnapshot.failure,
              ]),
            ),
          ),
        ),
        timeout(30000),
        catchError(() =>
          of(
            workspacesActions.workspaceSnapshot.failure(
              new Error("Snapshot service is not responding in time"),
            ),
          ),
        ),
      ),
    ),
  );
